import { useTranslation } from "react-i18next";
import Avatar from "../../../../parts/avatar";
import ToggleLinks from "../../../../parts/inputs/toggle-links-input";
import PaginationState from "../../../../parts/pagination-state";
import SvgIcon from "../../../../parts/SvgIcon";
import { formatNumber } from "../../../../utils/format-sub";
import CsvExportLink from "../../income/components/CsvExportLink";

// Colours live in subscribers-list.css keyed on data-status — the page's
// `.pill` rule outranks a utility class.
const StatusBadge = ({ status, noCardOnFile, t }) => (
  <span
    className="pill whitespace-nowrap"
    data-status={noCardOnFile ? "no_card" : status}
  >
    {noCardOnFile
      ? t("membershipList.paymentStatus.noCard")
      : t(`membershipList.paymentStatus.${status}`, { defaultValue: status })}
  </span>
);

const STATUS_FILTER_OPTIONS = [
  "active",
  "cancelling",
  "past_due",
  "trialing",
  "cancelled",
];

const SortableTh = ({ label, sortKey, sortConfig, handleSort, className }) => (
  <th className={className}>
    {label}{" "}
    <SvgIcon
      src="/img/svg/up_down_arrows.svg"
      onClickFn={() =>
        handleSort(
          { target: { value: sortKey } },
          sortConfig.key === sortKey && sortConfig.direction !== "descending"
            ? "descending"
            : "ascending"
        )
      }
    />
  </th>
);

const SubscribersTable = ({
  sortConfig,
  handleSort,
  subscribers,
  type,
  allActiveSubs,
  allChurnedSubs,
  onActivePageChanged,
  onChurnedPageChanged,
  csvString,
  nameString = "Subscriber",
  onDetailsClick,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  planFilter,
  setPlanFilter,
  planOptions = [],
  productType,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <>
      <header className="table-header">
        <hgroup>
          <input
            type="search"
            className="text-input"
            name="search"
            placeholder={t("membershipList.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-expandable="true"
          />
          <div className="table-toggle">
            <ToggleLinks
              name="type"
              options={[
                { text: t("membershipList.active"), value: "active" },
                { text: t("membershipList.churned"), value: "churned" },
              ]}
              defaultActive="active"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t("membershipList.allStatuses")}</option>
            {STATUS_FILTER_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {t(`membershipList.paymentStatus.${status}`)}
              </option>
            ))}
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">{t("membershipList.allPlans")}</option>
            {planOptions.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </hgroup>
        <CsvExportLink csv={csvString} filename={`${nameString}s.csv`} t={t} />
      </header>
      {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
      <section role="table-wrapper" data-borders-inside>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
        <table role="report-table" className="subscribers-table">
          {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
          <thead role="table-header">
            <tr>
              <SortableTh
                label={nameString}
                sortKey="customer_name"
                sortConfig={sortConfig}
                handleSort={handleSort}
              />
              <SortableTh
                label={t("membershipList.plan")}
                sortKey="plan.interval"
                sortConfig={sortConfig}
                handleSort={handleSort}
              />
              <SortableTh
                label={t("membershipList.joiningDate")}
                sortKey="createdAt"
                sortConfig={sortConfig}
                handleSort={handleSort}
                className="whitespace-nowrap"
              />
              <SortableTh
                label={t("membershipList.tenure")}
                sortKey="tenure"
                sortConfig={sortConfig}
                handleSort={handleSort}
              />
              <SortableTh
                label={t("membershipList.lastPayment")}
                sortKey="lastPaymentAt"
                sortConfig={sortConfig}
                handleSort={handleSort}
                className="whitespace-nowrap"
              />
              <SortableTh
                label={t("membershipList.lifetimeRevenue")}
                sortKey="lifetimeRevenue"
                sortConfig={sortConfig}
                handleSort={handleSort}
              />
              <th className="w-px">{t("membershipList.status")}</th>
              {productType === "subscription" && (
                <th>{t("membershipList.source")}</th>
              )}
              <th>{t("membershipList.email")}</th>
              {productType === "membership" && (
                <th>{t("membershipList.phoneNumber")}</th>
              )}
              <th className="text-center">{t("membershipList.action")}</th>
            </tr>
          </thead>
          {subscribers && subscribers.length > 0 ? (
            <BodyTableComponent
              subscribers={subscribers}
              productType={productType}
              onDetailsClick={onDetailsClick}
              detailsLabel={t("membershipList.details")}
              t={t}
            />
          ) : (
            <BodyTableEmptyStateComponent
              type={type}
              searchString={search}
              t={t}
            />
          )}
        </table>
      </section>
      <div className="table-section my-4">
        {allActiveSubs.length !== 0 && type !== "churned" && (
          <div className="pagination-wrapper">
            <PaginationState
              totalRecords={allActiveSubs.length}
              pageLimit={1000}
              pageNeighbours={1}
              onPageChanged={onActivePageChanged}
            />
          </div>
        )}
        {allChurnedSubs.length !== 0 && type === "churned" && (
          <div className="pagination-wrapper">
            <PaginationState
              totalRecords={allChurnedSubs.length}
              pageLimit={1000}
              pageNeighbours={1}
              onPageChanged={onChurnedPageChanged}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SubscribersTable;

const BodyTableComponent = ({
  subscribers,
  productType,
  onDetailsClick,
  detailsLabel,
  t,
}) => {
  return (
    <tbody>
      {subscribers.map((sub) => (
        <tr key={sub.id}>
          <td>
            <div className="flex items-center gap-2">
              <div className="image-wrapper">
                {sub.customer_avatar ? (
                  <Avatar size="32" url={sub.customer_avatar.url} user={{}} />
                ) : (
                  <div className="subscriber-icon"></div>
                )}
              </div>
              <span className="subscriber-text">{sub.customer_name}</span>
            </div>
          </td>
          <td>
            {sub.status === "trialing"
              ? t("membershipList.rollsOn", {
                  date: sub.current_period_end || "—",
                })
              : sub.plan.interval}
          </td>
          <td className="whitespace-nowrap">{sub.created}</td>
          <td className="whitespace-nowrap">
            {sub.tenure == null
              ? "—"
              : sub.tenure < 1
                ? t("membershipList.tenureUnderMonth")
                : t("membershipList.tenureMonths", { count: sub.tenure })}
          </td>
          <td className="whitespace-nowrap">
            {sub.lastPayment || sub.current_period_start}
          </td>
          <td>
            {sub.status === "trialing"
              ? "—"
              : formatNumber(sub.lifetimeRevenue || 0, sub.accountCurrency)}
          </td>
          <td>
            <div className="flex flex-col items-start gap-1">
              <StatusBadge
                status={sub.paymentStatus}
                noCardOnFile={sub.noCardOnFile}
                t={t}
              />
              {sub.retryStatus && (
                <span className="pill whitespace-nowrap" data-status="retrying">
                  {t("membershipList.retryingProgress", {
                    attempt: sub.retryStatus.attemptCount,
                  })}
                </span>
              )}
            </div>
          </td>
          {productType === "subscription" && (
            <td>
              <span className="pill">
                {t(
                  `membershipList.sourceLabel.${sub.source?.source || "direct"}`
                )}
              </span>
            </td>
          )}
          <td className="email">
            <a href={`mailto:${sub.customer_email}`}>{sub.customer_email}</a>
          </td>
          {productType === "membership" && <td>{sub.phoneNumber}</td>}
          <td>
            {/* Pass rows have no customer (they're one-time PaymentIntents,
                not a Stripe customer/subscription) — sub.customer is null,
                so opening the drawer here would have nothing to show. */}
            {/* A day pass has no MSB account, so nothing to open. */}
            {!!sub.customer && (
              <button
                type="button"
                onClick={(e) => {
                  onDetailsClick(e, sub.customer);
                }}
              >
                <span className="clipped p-0">
                  <b>{detailsLabel}</b>
                </span>
              </button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  );
};

const BodyTableEmptyStateComponent = ({ type, searchString, t }) => {
  return (
    <tbody>
      <tr>
        <td colSpan="100%">
          <div className="body-table-empty">
            {searchString ? (
              <>
                <span>{t("membershipList.noResultsFor")}</span>
                <p>{searchString}</p>
              </>
            ) : (
              <>
                <p>
                  {type !== "churned"
                    ? t("membershipList.noActiveSubscribers")
                    : t("membershipList.noChurnedSubscribers")}
                </p>
                <span>
                  {type !== "churned"
                    ? t("membershipList.noActiveSubscribersHint")
                    : t("membershipList.noChurnedSubscribersHint")}{" "}
                </span>
              </>
            )}
          </div>
        </td>
      </tr>
    </tbody>
  );
};
